// Placeholder scanning and replacement for `${...}` loop tokens.
// Example: `${bp.size}` resolves from loop scope values.
import { isLoopIdentifierStart } from './identifiers'
import { parseLoopPlaceholderAt } from './placeholder-parse'
import {
  buildInvalidLoopPlaceholderError,
  buildLegacyLoopPlaceholderError,
  buildMalformedLoopPlaceholderError,
} from './placeholder-errors'
import { resolveLoopPlaceholderValue } from './placeholder-resolve'
import { isSelectorPlaceholderContext, normalizeSelectorLoopValue } from './selector'
import type { LuisArrayVariables, LuisObjectVariables } from '../../types'
import type { LoopScope } from './types'
// Check placeholder scan flow.
// Check legacy placeholder errors.
// Resolve placeholder values.
// Continue selector placeholder flow.
// Return untouched source flow.
// Return diagnostic token flow.

// Replace `${...}` loop placeholders with current scope values.
export function replaceLoopPlaceholders(
  source: string,
  scope: LoopScope,
  arrays: LuisArrayVariables,
  variables: Record<string, string>,
  objectVariables: LuisObjectVariables,
): string {
  // Return source unchanged when placeholder marker is absent.
  if (!source.includes('$')) {
    // Return the original source when no placeholder markers exist.
    return source
  }

  const parts: string[] = []
  let segmentStart = 0

  // Scan source and resolve `${...}` placeholders without regex callbacks.
  for (let index = 0; index < source.length; index += 1) {
    const char = source[index]

    // Keep non-placeholder characters unchanged.
    if (char !== '$') {
      continue
    }

    // Reject unsupported plain `$name` loop placeholders.
    if (isLegacyLoopPlaceholderStart(source, index)) {
      throw new Error(buildLegacyLoopPlaceholderError(source, index))
    }

    const placeholder = parseLoopPlaceholderAt(source, index)

    // Reject malformed braced `${...}` placeholders immediately.
    if (!placeholder) {
      // Check whether current token started like a braced placeholder.
      if (source[index + 1] === '{') {
        throw new Error(buildMalformedLoopPlaceholderError(source, index))
      } else {
        // Continue literal `$` flow for non-placeholder tokens.
        continue
      }
    }

    const isSelectorContext = isSelectorPlaceholderContext(source, placeholder.start, placeholder.end)
    const value = resolveLoopPlaceholderValue(placeholder, scope, arrays, variables, objectVariables)

    // Reject placeholders that could not resolve from scope or expression parsing.
    if (value === null) {
      throw new Error(buildInvalidLoopPlaceholderError(placeholder.raw))
    }

    // Flush preceding segment and emit resolved value.
    if (index > segmentStart) {
      parts.push(source.slice(segmentStart, index))
    }

    // Normalize selector placeholders to safe selector token values.
    if (isSelectorContext) {
      parts.push(normalizeSelectorLoopValue(value))
    } else {
      // Use raw loop value outside selector contexts.
      parts.push(value)
    }

    index = placeholder.end - 1
    segmentStart = placeholder.end
  }

  // Return the original source when no placeholders resolved.
  if (segmentStart === 0) {
    // Return the untouched source text.
    return source
  }

  // Flush the remaining trailing source segment.
  if (segmentStart < source.length) {
    parts.push(source.slice(segmentStart))
  }

  // Return placeholder-resolved source text.
  return parts.join('')
}

// Check whether the current `$` starts an unsupported plain placeholder.
function isLegacyLoopPlaceholderStart(source: string, index: number): boolean {
  const next = source[index + 1] ?? ''

  // Return true only for plain `$name` placeholder heads.
  return next !== '{' && isLoopIdentifierStart(next)
}
